/**
 * Generische Pack/Einheiten-Umrechnung für Items, deren DB-`quantity` die Einzeleinheiten
 * speichert, Anzeige/Transfer aber in Packungen erfolgt — bisher nur für `blaettchen`
 * implementiert (siehe `blaettchen.ts`), aber `kapselhuelsen` ist backend-seitig strukturell
 * identisch (`KAPSELN_PER_PACKUNG=100`, `market.definitions.ts`) und brauchte laut Chris-Feedback
 * (2026-07-30) exakt dieselbe Behandlung in `lagerhaus.ts`. `blaettchen.ts` bleibt unverändert
 * (weiterhin genutzt von `rolltisch-tab.ts`) — dieses Modul ist die generische Erweiterung nur
 * für Call-Sites, die mehrere Pack-Items gleich behandeln müssen (aktuell nur `lagerhaus.ts`).
 */

export const PACK_UNITS_PER_ITEM: Readonly<Record<string, number>> = {
  blaettchen: 42,
  kapselhuelsen: 100,
  // NT-POLISH-01 (P7): Spiegel von ZIGARETTENHUELSEN_PRO_PAKET (market.definitions.ts) —
  // 1 Paket = 5 Schachteln × 50 Hülsen. quantity im Inventar = Einzelhülsen.
  zigarettenhuelsen: 250,
  // Pulver-Linie (Chris-Feedback 2026-07-30) — reine Anzeige-Konvention, kein Bezug zu einem
  // Kauf-/Liefer-Batch wie bei Blättchen/Kapselhülsen (Pulver-Linie wird gekocht, nicht gekauft).
  strassenpulver: 25,
  laborpulver: 10,
  reinsubstanz: 5,
};

/** Einheiten-Schwellenwerte für Badge-Farbcodierung (identisch zu blaettchen.ts). */
export const PACK_BADGE_LOW_THRESHOLD = 10;
export const PACK_BADGE_MED_THRESHOLD = 25;

export function getPackUnitsPerPack(itemKey: string): number | null {
  return PACK_UNITS_PER_ITEM[itemKey] ?? null;
}

/** Ist dieser Item-Key ein Pack-Item (Anzeige/Transfer in Packungen statt Einzeleinheiten)? */
export function isPackItem(itemKey: string): boolean {
  return itemKey in PACK_UNITS_PER_ITEM;
}

/** ceil(totalUnits / unitsPerPack) — Nicht-Pack-Items liefern 0. */
export function packCount(itemKey: string, totalUnits: number): number {
  const unitsPerPack = getPackUnitsPerPack(itemKey);
  if (unitsPerPack === null || totalUnits <= 0) return 0;
  return Math.ceil(totalUnits / unitsPerPack);
}

/** floor(totalUnits / unitsPerPack) — Anzahl voller Packungen. */
export function fullPackCount(itemKey: string, totalUnits: number): number {
  const unitsPerPack = getPackUnitsPerPack(itemKey);
  if (unitsPerPack === null) return 0;
  return Math.floor(totalUnits / unitsPerPack);
}

/**
 * Konvertiert eine Pack-Anzahl (aus der UI) in Einzeleinheiten für den Backend-API-Call.
 * Angebrochene Packung geht immer als Einheit mit (identische Regel wie
 * `blaettchenPacksToSheets`). Nicht-Pack-Items geben `packs` unverändert zurück.
 */
export function packsToUnits(itemKey: string, packs: number, totalUnits: number): number {
  const unitsPerPack = getPackUnitsPerPack(itemKey);
  if (unitsPerPack === null) return packs;
  const full = fullPackCount(itemKey, totalUnits);
  if (packs <= full) return packs * unitsPerPack;
  return totalUnits;
}

/** CSS-Custom-Property-Name für Badge-Farbe basierend auf Gesamt-Einheiten-Anzahl. */
export function packBadgeCssVar(totalUnits: number): string {
  if (totalUnits < PACK_BADGE_LOW_THRESHOLD) return '--accent-red';
  if (totalUnits < PACK_BADGE_MED_THRESHOLD) return '--accent-gold';
  return '--accent-green';
}
